import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(null);

export const THEMES = {
  DARK: 'dark',
  LIGHT: 'light',
  SYSTEM: 'system',
};

const getSystemTheme = () =>
  (typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? THEMES.DARK
    : THEMES.LIGHT;

export const ThemeProvider = ({ children }) => {
  // `theme` is the user's CHOICE (dark | light | system). Default: dark (brand).
  const [theme, setThemeState] = useState(() => localStorage.getItem('qnneal-theme') || THEMES.DARK);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  // Live-follow the OS preference while choice === 'system'.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemTheme(e.matches ? THEMES.DARK : THEMES.LIGHT);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler); };
  }, []);

  const resolvedTheme = theme === THEMES.SYSTEM ? systemTheme : theme;

  useEffect(() => {
    localStorage.setItem('qnneal-theme', theme);
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.documentElement.classList.toggle('dark', resolvedTheme === THEMES.DARK);
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', resolvedTheme === THEMES.DARK ? '#0A0E1A' : '#F8FAFC');
  }, [theme, resolvedTheme]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((p) => (p === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK)), []);
  // dark → light → system → dark
  const cycleTheme = useCallback(
    () => setThemeState((p) =>
      p === THEMES.DARK ? THEMES.LIGHT : p === THEMES.LIGHT ? THEMES.SYSTEM : THEMES.DARK), []);

  return (
    <ThemeContext.Provider value={{
      theme, resolvedTheme, systemTheme, setTheme, toggleTheme, cycleTheme,
      isDark: resolvedTheme === THEMES.DARK, isLight: resolvedTheme === THEMES.LIGHT,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
